import { DataType, FunctionDataType, FunctionDefinition, FunctionPortDefinition, typeAMatchesTypeB } from '@glueapp/graph-types';
export interface CompatibilityInfo {
    function: FunctionDefinition;
    compatible: boolean;
    missingInputs: FunctionPortDefinition[];
    missingOutputs: FunctionPortDefinition[];
    incompatibleInputs: { portName: string; expectedType: DataType }[];
    incompatibleOutputs: { portName: string; expectedType: DataType }[];
}

export function computeCompatibilityInfo(func: FunctionDefinition, expectedType: FunctionDataType): CompatibilityInfo {
    const funcType: FunctionDataType = {
        kind: 'function',
        inputTypes: Object.fromEntries(func.inputPortDefinitions.map(port => [port.name, port.type])),
        outputTypes: Object.fromEntries(func.outputPortDefinitions.map(port => [port.name, port.type])),
    };

    const isCompatible = typeAMatchesTypeB(funcType, expectedType);

    const missingInputs: FunctionPortDefinition[] = [];
    const incompatibleInputs: { portName: string; expectedType: DataType }[] = [];
    for (const [portName, portType] of Object.entries(expectedType.inputTypes)) {
        const existingPort = func.inputPortDefinitions.find(p => p.name === portName);
        if (!existingPort) {
            missingInputs.push({
                id: `input_${portName}_${Date.now()}`,
                name: portName,
                type: portType,
            });
        } else if (!typeAMatchesTypeB(existingPort.type, portType)) {
            // Port exists but type doesn't match
            incompatibleInputs.push({ portName, expectedType: portType });
        }
    }

    const missingOutputs: FunctionPortDefinition[] = [];
    const incompatibleOutputs: { portName: string; expectedType: DataType }[] = [];
    for (const [portName, portType] of Object.entries(expectedType.outputTypes)) {
        const existingPort = func.outputPortDefinitions.find(p => p.name === portName);
        if (!existingPort) {
            missingOutputs.push({
                id: `output_${portName}_${Date.now()}`,
                name: portName,
                type: portType,
            });
        } else if (!typeAMatchesTypeB(existingPort.type, portType)) {
            // Port exists but type doesn't match
            incompatibleOutputs.push({ portName, expectedType: portType });
        }
    }

    return {
        function: func,
        compatible: isCompatible,
        missingInputs,
        missingOutputs,
        incompatibleInputs,
        incompatibleOutputs,
    };
}

export function createFixedFunction(func: FunctionDefinition, info: CompatibilityInfo): FunctionDefinition {
    // Fix inputs: update types of incompatible ones and add missing ones
    const fixedInputs = func.inputPortDefinitions.map(port => {
        const incompatible = info.incompatibleInputs.find(i => i.portName === port.name);
        if (incompatible) {
            return { ...port, type: incompatible.expectedType };
        }
        return port;
    });

    // Fix outputs: update types of incompatible ones and add missing ones
    const fixedOutputs = func.outputPortDefinitions.map(port => {
        const incompatible = info.incompatibleOutputs.find(o => o.portName === port.name);
        if (incompatible) {
            return { ...port, type: incompatible.expectedType };
        }
        return port;
    });

    return {
        ...func,
        inputPortDefinitions: [...fixedInputs, ...info.missingInputs],
        outputPortDefinitions: [...fixedOutputs, ...info.missingOutputs],
        updatedAt: new Date(),
        version: func.version + 1,
    };
}
